import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  Mail,
  Phone,
  MapPin,
  Facebook,
  Instagram,
  Linkedin,
  Twitter,
  ChevronRight,
  ArrowUp,
  Send,
} from "lucide-react";
import { companyInfo } from "../data/mock";

const Footer = () => {
  const [email, setEmail] = useState("");
  const currentYear = new Date().getFullYear();

  // Helper function to scroll to top for non-hash links
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <footer className="bg-[#050914] text-gray-300 relative overflow-hidden border-t border-gray-900/50">
      {/* --- Advanced Ambient Background Effects --- */}
      <div className="absolute top-0 left-0 w-full h-[2px] bg-gradient-to-r from-transparent via-maroon-600 to-transparent opacity-70"></div>

      {/* Glowing Orbs */}
      <div className="absolute -top-40 -right-40 w-[800px] h-[800px] bg-maroon-900/10 rounded-full blur-[150px] pointer-events-none mix-blend-screen"></div>
      <div className="absolute bottom-0 -left-40 w-[600px] h-[600px] bg-rose-900/10 rounded-full blur-[120px] pointer-events-none mix-blend-screen"></div>

      {/* Grid Pattern Overlay (Subtle) */}
      <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAiIGhlaWdodD0iMjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGNpcmNsZSBjeD0iMiIgY3k9IjIiIHI9IjEiIGZpbGw9InJnYmEoMjU1LDQ0LDg1LDAuMDcpIi8+PC9zdmc+')] pointer-events-none opacity-50"></div>

      <div className="max-w-8xl mx-auto px-6 md:px-8 lg:px-16 xl:px-16 pt-16 pb-6 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-12 lg:gap-8 xl:gap-12">
          {/* 1. Brand, Newsletter & Socials (Takes up 4 columns on large screens) */}
          <div className="lg:col-span-4 flex flex-col space-y-8">
            {/* White Background Logo Container */}
            <Link
              to="/"
              onClick={scrollToTop}
              className="inline-block group self-start"
            >
              <div className="bg-white p-4 rounded-2xl shadow-[0_8px_30px_rgba(255,255,255,0.05)] group-hover:shadow-[0_8px_30px_rgba(159,18,57,0.2)] transition-all duration-500 border border-gray-200">
                <img
                  src="/logo-horizontal.png"
                  alt="Maroons Business Solutions"
                  className="h-12 md:h-14 w-auto object-contain group-hover:scale-105 transition-transform duration-500"
                />
              </div>
            </Link>

            <p className="text-sm md:text-base leading-relaxed text-gray-400 font-medium max-w-md">
              {companyInfo?.tagline ||
                "Comprehensive solutions designed to engineer resilient foundations, scale operations, and drive measurable commercial growth."}
            </p>

            {/* Newsletter Subscription (Added for a complete advanced feel) */}
            <div className="space-y-3 max-w-md">
              <h4 className="text-white text-sm font-bold tracking-wider uppercase">
                Stay Updated
              </h4>

              <div className="relative group">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email address"
                  className="w-full bg-gray-900/60 border border-gray-800 rounded-xl py-3 pl-4 pr-12 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-maroon-500 focus:ring-1 focus:ring-maroon-500 transition-all backdrop-blur-sm"
                />

                <button
                  onClick={() => {
                    if (!email) return alert("Please enter an email address.");
                    window.location.href = `mailto:info@maroons.ai?subject=Newsletter Subscription&body=Email: ${email}`;
                  }}
                  className="absolute right-2 top-1/2 -translate-y-1/2 bg-maroon-600 hover:bg-maroon-500 text-white p-1.5 rounded-lg transition-colors"
                >
                  <Send className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Premium Social Icons */}
            <div className="flex flex-wrap gap-3 pt-2">
              {[
                {
                  icon: Facebook,
                  link: "https://www.facebook.com/maroonsb2b/",
                },
                {
                  icon: Instagram,
                  link: "https://www.instagram.com/maroonsbusinesssolutions/",
                },
                {
                  icon: Linkedin,
                  link: "https://www.linkedin.com/in/maroons-business-solutions-b4b2023b7/",
                },
                { icon: Twitter, link: "https://x.com/MaroonsBusines" },
              ].map((social, idx) => (
                <a
                  key={idx}
                  href={social.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-gray-900/80 backdrop-blur-sm p-2.5 rounded-xl border border-gray-800/80 text-gray-400 hover:text-white hover:border-maroon-500/50 hover:bg-gradient-to-br hover:from-maroon-600 hover:to-rose-800 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_10px_20px_rgba(159,18,57,0.3)] group"
                >
                  <social.icon className="h-5 w-5 group-hover:scale-110 transition-transform" />
                </a>
              ))}
            </div>
          </div>

          {/* 2. Quick Links (2 columns) */}
          <div className="lg:col-span-2 lg:ml-auto">
            <h3 className="text-white font-bold text-lg mb-6 tracking-wide flex items-center">
              <span className="w-2 h-2 rounded-full bg-maroon-600 mr-3"></span>
              Company
            </h3>
            <ul className="space-y-3.5">
              {[
                { name: "Home", path: "/" },
                { name: "About Us", path: "/about" },
                { name: "Our Team", path: "/team" },
                { name: "Calculators", path: "/calculators" },
                { name: "Contact Us", path: "/contact" },
              ].map((link, idx) => (
                <li key={idx}>
                  <Link
                    to={link.path}
                    onClick={scrollToTop}
                    className="text-gray-400 hover:text-maroon-400 flex items-center group transition-colors text-sm font-medium"
                  >
                    <ChevronRight className="h-4 w-4 mr-2 text-maroon-600/0 -ml-6 group-hover:text-maroon-500 group-hover:opacity-100 group-hover:ml-0 transition-all duration-300 ease-out" />
                    <span className="group-hover:translate-x-1 transition-transform duration-300">
                      {link.name}
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* 3. Services / Expertise (3 columns) */}
          <div className="lg:col-span-3">
            <h3 className="text-white font-bold text-lg mb-6 tracking-wide flex items-center">
              <span className="w-2 h-2 rounded-full bg-rose-600 mr-3"></span>
              Expertise
            </h3>
            <ul className="space-y-3.5">
              {[
                { name: "Strategy & Operations", hash: "strategy-operations" },
                { name: "Software Development", hash: "software-development" },
                { name: "Finance & Tax Planning", hash: "finance-tax" },
                { name: "Marketing & Sales", hash: "marketing-sales" },
                { name: "Startup Incubation", hash: "startup-incubation" },
              ].map((service, idx) => (
                <li key={idx}>
                  <Link
                    to={`/services#${service.hash}`}
                    className="text-gray-400 hover:text-rose-400 flex items-center group transition-colors text-sm font-medium"
                  >
                    <ChevronRight className="h-4 w-4 mr-2 text-rose-600/0 -ml-6 group-hover:text-rose-500 group-hover:opacity-100 group-hover:ml-0 transition-all duration-300 ease-out" />
                    <span className="group-hover:translate-x-1 transition-transform duration-300">
                      {service.name}
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* 4. Glassmorphism Contact Info (3 columns) */}
          <div className="lg:col-span-3">
            <h3 className="text-white font-bold text-lg mb-6 tracking-wide flex items-center">
              <span className="w-2 h-2 rounded-full bg-gray-400 mr-3"></span>
              Get in Touch
            </h3>
            <ul className="space-y-4">
              {/* Address Card */}
              <li className="flex items-start space-x-4 bg-gradient-to-br from-gray-900/80 to-gray-900/30 backdrop-blur-md p-4 rounded-2xl border border-gray-800/60 hover:border-maroon-600/50 hover:bg-gray-800/50 transition-all duration-300 group shadow-lg">
                <div className="bg-gray-950/80 p-2.5 rounded-xl border border-gray-800 group-hover:border-maroon-500 group-hover:bg-maroon-600 transition-colors shrink-0">
                  <MapPin className="h-4 w-4 text-gray-400 group-hover:text-white transition-colors" />
                </div>
                <span className="text-sm text-gray-300 leading-relaxed font-medium pt-0.5">
                  {companyInfo?.address}
                </span>
              </li>

              {/* Phone Card */}
              <li className="flex items-start space-x-4 bg-gradient-to-br from-gray-900/80 to-gray-900/30 backdrop-blur-md p-4 rounded-2xl border border-gray-800/60 hover:border-maroon-600/50 hover:bg-gray-800/50 transition-all duration-300 group shadow-lg">
                <div className="bg-gray-950/80 p-2.5 rounded-xl border border-gray-800 group-hover:border-maroon-500 group-hover:bg-maroon-600 transition-colors shrink-0">
                  <Phone className="h-4 w-4 text-gray-400 group-hover:text-white transition-colors" />
                </div>
                <div className="space-y-1 pt-0.5">
                  <a
                    href={`tel:${companyInfo?.phone}`}
                    className="block text-sm text-gray-300 hover:text-white transition-colors font-bold tracking-wide"
                  >
                    {companyInfo?.phone}
                  </a>
                  {companyInfo?.phone2 && (
                    <a
                      href={`tel:${companyInfo?.phone2}`}
                      className="block text-sm text-gray-400 hover:text-white transition-colors font-semibold tracking-wide"
                    >
                      {companyInfo.phone2}
                    </a>
                  )}
                </div>
              </li>

              {/* Email Card */}
              <li className="flex items-center space-x-4 bg-gradient-to-br from-gray-900/80 to-gray-900/30 backdrop-blur-md p-4 rounded-2xl border border-gray-800/60 hover:border-maroon-600/50 hover:bg-gray-800/50 transition-all duration-300 group shadow-lg">
                <div className="bg-gray-950/80 p-2.5 rounded-xl border border-gray-800 group-hover:border-maroon-500 group-hover:bg-maroon-600 transition-colors shrink-0">
                  <Mail className="h-4 w-4 text-gray-400 group-hover:text-white transition-colors" />
                </div>
                <a
                  href={`mailto:${companyInfo?.email}`}
                  className="text-sm text-gray-300 hover:text-white transition-colors font-medium break-all"
                >
                  {companyInfo?.email}
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* --- Bottom Bar --- */}
        <div className="mt-20 pt-8 border-t border-gray-800/60 flex flex-col md:flex-row justify-between items-center gap-6 relative">
          <p className="text-sm text-gray-500 font-medium text-center md:text-left">
            © {currentYear}{" "}
            <span className="text-gray-300">
              {companyInfo?.name || "Maroons Business Solutions"}
            </span>
            . All rights reserved.
          </p>

          <div className="flex flex-wrap justify-center items-center gap-x-6 gap-y-2 text-sm text-gray-500 font-medium">
            <Link
              to="/privacy"
              onClick={scrollToTop}
              className="hover:text-maroon-400 transition-colors"
            >
              Privacy Policy
            </Link>
            <span className="hidden md:inline-block w-1.5 h-1.5 rounded-full bg-gray-700"></span>
            <Link
              to="/terms"
              onClick={scrollToTop}
              className="hover:text-maroon-400 transition-colors"
            >
              Terms of Service
            </Link>
            <span className="hidden md:inline-block w-1.5 h-1.5 rounded-full bg-gray-700"></span>
            <Link
              to="/sitemap"
              onClick={scrollToTop}
              className="hover:text-maroon-400 transition-colors"
            >
              Sitemap
            </Link>
          </div>

          {/* Floating Back to Top Button */}
          <button
            onClick={scrollToTop}
            className="absolute right-0 -top-6 bg-gray-900 border border-gray-700 hover:border-maroon-500 hover:bg-maroon-600 text-gray-400 hover:text-white p-3 rounded-xl shadow-[0_4px_20px_rgba(0,0,0,0.5)] transition-all duration-300 group z-20"
            aria-label="Back to top"
          >
            <ArrowUp className="h-5 w-5 group-hover:-translate-y-1 transition-transform" />
          </button>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
